import { DIRECTIONS, type Direction, type Tile, type Vec2 } from "./types";

/** Unit step for each direction. `y` grows southward, so "up" is -1. */
const DELTAS: Readonly<Record<Direction, Readonly<Tile>>> = {
  up: { x: 0, y: -1 },
  left: { x: -1, y: 0 },
  down: { x: 0, y: 1 },
  right: { x: 1, y: 0 },
};

const OPPOSITES: Readonly<Record<Direction, Direction>> = {
  up: "down",
  left: "right",
  down: "up",
  right: "left",
};

export const delta = (dir: Direction): Tile => DELTAS[dir];

export const opposite = (dir: Direction): Direction => OPPOSITES[dir];

/** Tile `n` steps from `tile` along `dir`. */
export const step = (tile: Tile, dir: Direction, n = 1): Tile => ({
  x: tile.x + DELTAS[dir].x * n,
  y: tile.y + DELTAS[dir].y * n,
});

/**
 * Squared distance between tiles.
 *
 * The arcade compares squared distances to avoid a square root, and so do we:
 * it is exact in integers, which keeps tie-breaks identical to the original.
 */
export const distanceSquared = (a: Tile, b: Tile): number => {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
};

export const sameTile = (a: Tile, b: Tile): boolean => a.x === b.x && a.y === b.y;

/** The tile containing a continuous position. */
export const tileAt = (pos: Vec2): Tile => ({ x: Math.round(pos.x), y: Math.round(pos.y) });

/** Heading a position holds after moving `dist` along `dir`. */
export const advance = (pos: Vec2, dir: Direction, dist: number): Vec2 => ({
  x: pos.x + DELTAS[dir].x * dist,
  y: pos.y + DELTAS[dir].y * dist,
});

/** How far `pos` sits from the centre of its tile, along the axis of `dir`. */
export const offsetFromCentre = (pos: Vec2, dir: Direction): number => {
  const tile = tileAt(pos);
  return DELTAS[dir].x !== 0 ? (pos.x - tile.x) * DELTAS[dir].x : (pos.y - tile.y) * DELTAS[dir].y;
};

/** Angle in radians for a facing, for the renderer. 0 = facing +x (east). */
export const headingRadians = (dir: Direction): number => Math.atan2(-DELTAS[dir].y, DELTAS[dir].x);

/**
 * Where a grid direction points ON SCREEN, under an isometric camera.
 *
 * Nothing in this maze points up, down, left or right on screen. The camera
 * sits on a 45-degree yaw, so grid east renders as screen down-right and grid
 * south as down-left. A touch control that assumed otherwise would send the
 * astronaut off at 45 degrees to wherever the player pointed, which is the
 * whole reason this exists rather than a table of four arrow glyphs.
 *
 * Radians clockwise from screen-right, matching `atan2(y, x)` in DOM
 * coordinates, where `y` grows downward.
 */
export const screenBearing = (dir: Direction, yaw: number, pitch: number): number => {
  const { x, y } = DELTAS[dir];
  // The camera's right axis is (sin yaw, 0, -cos yaw); its screen-down axis is
  // the ground plane flattened by sin(pitch). The diamond is squashed, not
  // square, and the glyphs have to lean with it.
  return Math.atan2(
    Math.sin(pitch) * (x * Math.cos(yaw) + y * Math.sin(yaw)),
    x * Math.sin(yaw) - y * Math.cos(yaw),
  );
};

/**
 * The grid direction a screen-space drag points at, or null for no movement.
 *
 * Compares bearings rather than raw dot products so the vertical squash cannot
 * bias the answer toward whichever pair of directions happens to carry more
 * screen travel per tile.
 */
export const directionFromScreen = (dx: number, dy: number, yaw: number, pitch: number): Direction | null => {
  if (dx === 0 && dy === 0) return null;
  const drag = Math.atan2(dy, dx);
  let best: Direction | null = null;
  let closest = Infinity;
  for (const dir of DIRECTIONS) {
    const bearing = screenBearing(dir, yaw, pitch);
    // Shortest signed arc, so a drag at 179 degrees reads as near -179.
    const off = Math.abs(Math.atan2(Math.sin(drag - bearing), Math.cos(drag - bearing)));
    if (off < closest) {
      closest = off;
      best = dir;
    }
  }
  return best;
};
